import { CitationInfo } from './types';
import { CitationTargets } from './markdown';

/**
 * Citations (chat-ui-spec §6.2–6.3, contract §3.5 and §8.2). A marker links to the CENTERLINE page
 * its passage came from when the ids allow one; otherwise it opens a details card listing them.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const uuid = (value: unknown): string | null =>
  typeof value === 'string' && UUID_RE.test(value) ? value : null;

/** Document type name → URL slug, `documentTypeToUrl` in scripts/utils, passed in from the page. */
export type DocumentTypeSlugs = Readonly<Record<string, string>>;

/**
 * The CENTERLINE page for a citation, or null when no link can be built:
 * 1. a document id and a known document type → that document;
 * 2. a plain file id → the PDF viewer (it has no page parameter yet);
 * 3. a received-email id → the project's correspondence log, opened on that email;
 * 4. anything else, including email-attachment ids (`<uuid>_email_uploads_N`, which are not
 *    CENTERLINE file ids) → null, which means the details card.
 * Ids are used only when they are UUIDs, so nothing the stream says can shape the path.
 */
export function citationHref(
  info: Partial<CitationInfo>,
  slugs: DocumentTypeSlugs,
  fallbackProjectId?: string | null,
): string | null {
  const projectId = uuid(info.projectId) ?? uuid(fallbackProjectId);
  const documentId = uuid(info.documentId);
  const slug = info.docType ? slugs[info.docType] : undefined;
  if (projectId && documentId && slug && /^[a-z0-9-]+$/.test(slug)) {
    return `/main/projects/${projectId}/documents/${slug}/${documentId}`;
  }
  const fileId = uuid(info.fileId);
  if (fileId) return `/main/pdf/${fileId}`;
  const emailId = uuid(info.receivedEmailId);
  if (projectId && emailId) return `/main/projects/${projectId}/correspondence-log?id=${emailId}`;
  return null;
}

/** Number → link or null (the card), for every citation the turn numbered. */
export function citeTargets(
  citations: Partial<CitationInfo>[] | null | undefined,
  slugs: DocumentTypeSlugs,
  fallbackProjectId?: string | null,
): CitationTargets {
  const targets = new Map<number, string | null>();
  (citations ?? []).forEach((info) => {
    if (typeof info.n !== 'number' || !Number.isFinite(info.n)) return;
    targets.set(info.n, citationHref(info, slugs, fallbackProjectId));
  });
  return targets;
}

/** The citation for a number, if the turn has one. */
export const citationByNumber = (
  citations: Partial<CitationInfo>[] | null | undefined,
  n: number,
): Partial<CitationInfo> | undefined => (citations ?? []).find((c) => c.n === n);

/** Whether the card has anything to show: at least one of the five identifying fields. */
export function isInspectable(info: Partial<CitationInfo> | undefined): boolean {
  if (!info) return false;
  return Boolean(
    info.fileName || info.fileId || info.receivedEmailId || info.documentId || info.projectId,
  );
}

export interface CitationField {
  label: string;
  value: string;
  /** Monospace and copyable. */
  isId: boolean;
}

/** The card's rows, in display order, skipping empty values (`page` shows whenever not null). */
export function citationFields(info: Partial<CitationInfo>): CitationField[] {
  const rows: Array<[string, unknown, boolean]> = [
    ['file name', info.fileName, false],
    ['doc_type', info.docType, false],
    ['file category', info.fileCategory, false],
    ['clause', info.clauseId, false],
    ['sheet', info.sheetNumber, false],
    ['page', info.page, false],
    ['lane', info.source, false],
    ['file id', info.fileId, true],
    ['email id', info.receivedEmailId, true],
    ['document id', info.documentId, true],
    ['project id', info.projectId, true],
  ];
  return rows
    .filter(([label, value]) =>
      label === 'page' ? value !== null && value !== undefined : Boolean(value),
    )
    .map(([label, value, isId]) => ({ label, value: String(value), isId }));
}

export const CITATION_CARD_NOTE =
  'No link is available for this passage. These are the ids it came from.';
